/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

/**
 * Generate and download a printable PDF of a signed prescription.
 * The QR contains an itemised JSON payload for machine-readable verification.
 */
export const downloadPrescriptionPdf = async (doc) => {
  if (!doc) return;
  const rx = doc.prescription || {};
  const items = rx.items || [];
  const nre = rx.nre || '';
  const checksum = doc.checksum_sha256 || '';

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 48;
  const width = pdf.internal.pageSize.getWidth() - marginX * 2;
  let y = 56;
  const line = (h = 14) => { y += h; };
  const check = () => {
    if (y > pdf.internal.pageSize.getHeight() - 80) { pdf.addPage(); y = 56; }
  };

  // Header
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18);
  pdf.text('Ricetta Rossa Elettronica (SSN)', marginX, y); line(20);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(11);
  pdf.text('Documento CDA R2 · LOINC 57833-6', marginX, y); line();

  // Prescription header info
  pdf.setDrawColor(200);
  pdf.line(marginX, y, marginX + width, y); line(10);
  pdf.setFont('helvetica', 'bold'); pdf.text('NRE:', marginX, y);
  pdf.setFont('helvetica', 'normal'); pdf.text(nre, marginX + 40, y); line();
  if (doc.created_at) {
    pdf.setFont('helvetica', 'bold'); pdf.text('Emessa il:', marginX, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(new Date(doc.created_at).toLocaleString('it-IT'), marginX + 80, y); line();
  }
  if (doc.signed_at) {
    pdf.setFont('helvetica', 'bold'); pdf.text('Firmata il:', marginX, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(new Date(doc.signed_at).toLocaleString('it-IT'), marginX + 80, y); line();
  }
  if (doc.exported_at) {
    pdf.setFont('helvetica', 'bold'); pdf.text('Trasmessa il:', marginX, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(new Date(doc.exported_at).toLocaleString('it-IT') + ' (SISS sim)', marginX + 90, y); line();
  }
  if (rx.exemption_code) {
    pdf.setFont('helvetica', 'bold'); pdf.text('Esenzione:', marginX, y);
    pdf.setFont('helvetica', 'normal'); pdf.text(String(rx.exemption_code), marginX + 80, y); line();
  }
  line(6);

  // Diagnosis
  if (rx.diagnosis) {
    pdf.setFont('helvetica', 'bold'); pdf.text('Diagnosi', marginX, y); line();
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(rx.diagnosis, width);
    lines.forEach((ln) => { check(); pdf.text(ln, marginX, y); line(); });
    line(6);
  }

  // Medicines list
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
  pdf.text(`Farmaci prescritti (${items.length})`, marginX, y); line(18);
  pdf.setFontSize(11);
  items.forEach((it, idx) => {
    check();
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${idx + 1}. ${it.name || ''}`, marginX, y); line();
    pdf.setFont('helvetica', 'normal');
    if (it.aic) { pdf.text(`   AIC ${it.aic}`, marginX, y); line(12); }
    if (it.quantity) { pdf.text(`   Quantità: ${it.quantity}`, marginX, y); line(12); }
    if (it.posology) {
      const lines = pdf.splitTextToSize(`   Posologia: ${it.posology}`, width - 12);
      lines.forEach((ln) => { check(); pdf.text(ln, marginX, y); line(12); });
    }
    if (it.notes) {
      pdf.setFont('helvetica', 'italic');
      const nlines = pdf.splitTextToSize(`   Note: ${it.notes}`, width - 12);
      nlines.forEach((ln) => { check(); pdf.text(ln, marginX, y); line(12); });
      pdf.setFont('helvetica', 'normal');
    }
    line(6);
  });

  if (rx.notes) {
    check();
    pdf.setFont('helvetica', 'bold'); pdf.text('Note aggiuntive', marginX, y); line();
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(rx.notes, width);
    lines.forEach((ln) => { check(); pdf.text(ln, marginX, y); line(12); });
    line(6);
  }

  // Signature receipt block
  if (doc.signature_receipt) {
    check();
    pdf.setDrawColor(200);
    pdf.line(marginX, y, marginX + width, y); line(10);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
    pdf.text('Ricevuta di firma elettronica', marginX, y); line();
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    pdf.text(`Metodo: ${doc.signature_method || 'otp_email'}`, marginX, y); line(11);
    const rec = doc.signature_receipt || '';
    pdf.text(`Receipt: ${rec.slice(0, 32)}…${rec.slice(-16)}`, marginX, y); line(11);
    if (checksum) {
      pdf.text(`Checksum XML: ${checksum.slice(0, 32)}…${checksum.slice(-16)}`, marginX, y); line(11);
    }
  }

  // ---- QR code with verifiable payload ----
  const qrPayload = {
    v: 1,
    kind: 'rre',
    nre,
    doc_id: doc.id,
    checksum: checksum ? checksum.slice(0, 32) : null,
    signed_at: doc.signed_at || null,
    items: items.map((it) => ({ n: it.name, q: it.quantity, aic: it.aic || null })),
  };
  try {
    const qrDataUrl = await QRCode.toDataURL(JSON.stringify(qrPayload), {
      errorCorrectionLevel: 'M', margin: 1, width: 180,
    });
    // Place QR in the bottom-right of the LAST page
    const pageH = pdf.internal.pageSize.getHeight();
    const pageW = pdf.internal.pageSize.getWidth();
    const size = 110;
    pdf.addImage(qrDataUrl, 'PNG', pageW - marginX - size, pageH - size - 60, size, size);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(120);
    pdf.text('Scansiona per verifica NRE',
             pageW - marginX - size, pageH - 50);
    pdf.setTextColor(0);
  } catch (e) {
    // QR failure should not block the PDF
     
    console.warn('QR generation failed', e);
  }

  // Footer on every page
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(140);
    pdf.text(`SanitaNexus · Ricetta ${nre || ''} · pagina ${i}/${totalPages}`,
             marginX, pdf.internal.pageSize.getHeight() - 20);
    pdf.setTextColor(0);
  }

  const stamp = doc.signed_at || doc.created_at
    ? new Date(doc.signed_at || doc.created_at).toISOString().slice(0, 10)
    : 'ricetta';
  pdf.save(`ricetta-${nre || 'rre'}-${stamp}.pdf`);
};
